import styled from "styled-components";
import WSTabs from "./bottom-tabs/w_s_tabs";
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState
} from "react";
import {
  AddAbsenceManagementInfoMutationVariables,
  AddAutonomyWorkingHourInfoMutationVariables,
  AddControlNotificationMutationVariables,
  AddEtcOperatingHoursMutationVariables,
  AddSelfDeterminedWorkHoursMutationVariables,
  AddWidgetSettingInfoMutationVariables,
  Day_Type,
  Exact,
  Is_Use,
  OtWorkingFlexibleBasicEntity,
  OtWorkingFlexibleHoursEntity,
  OtWorkingStandardHoursEntity,
  Setting_Enable_For_Working_Template,
  Work_Type,
  useAddAbsenceManagementInfoMutation,
  useAddAutonomyWorkingHourInfoMutation,
  useAddControlNotificationMutation,
  useAddEtcOperatingHoursMutation,
  useAddFlexibleWorkingHourInfoMutation,
  useAddSelfDeterminedWorkHoursMutation,
  useAddStandardWtBasicInfoMutation,
  useAddWidgetSettingInfoMutation,
  useAddWtBasicInfoMutation,
  useUpdateWtBasicInfoMutation
} from "../../../../generated/graphql";
import { useParams } from "react-router-dom";
import handleApolloErrorLog from "../../../../Utils/handle_apollo_error_log/handle_apollo_error_log";
import handleErrorLog from "../../../../Utils/handle_error_log/handle_error_log";
import ReactLoading from "react-loading";
import { colors } from "../../../../components/GlobalStyle/GlobalStyle";
import useAsonicWorkingSystem, {
  IWorkPolicyAdditionalSetting,
  IWorkPolicyBasicSetting
} from "../../../../hooks/use_asonic_working_system/use_asonic_working_system";
import TopTabs from "./top-tabs/top-tabs";
import ToastMessage, {
  MessageTypes
} from "../../../../components/toast-message/toast-message";
import useOpenToastMessage from "../../../../hooks/toast-message-hook/use-open-toast-message";
import type { WorkingTemplateBasePolicyController } from "../../../../components/w_s_tabs/work_time_tab/management-policy/use-working-template-base-policy";
import { useWorkingTemplateBasePolicy } from "../../../../components/w_s_tabs/work_time_tab/management-policy/use-working-template-base-policy";
import workingTemplatePolicyApi from "../../../../api/working-template-policy-api";
import {
  saveWorkingTemplatePolicyBinding,
  shouldPersistBasePolicyBinding,
  WorkingTemplateSaveStage,
  WorkingTemplatePolicySaveValidationError
} from "./working-template-save-orchestrator";
import workingTemplateCommuteSettingsApi from "../../../../api/working-template-commute-settings-api";
import {
  CommuteHydrationStatus,
  CommuteSettingsValidationError,
  WorkingTemplateCommuteSettingsDraft
} from "../../../../components/w_s_tabs/recording_time/commute-settings.model";
import {
  createWorkingTemplateCommuteSaveCoordinator,
  WorkingTemplateCommuteSaveStage
} from "./working-template-commute-save-orchestrator";

interface IReducer {
  day: Day_Type;
}

interface IWSStore extends IReducer {
  dispatch?: React.Dispatch<{
    type: Day_Type;
  }>;
}

export const SELECTED_SUB_TAB = {
  WORKING_TIME: "WORKING_TIME",
  RECORDING_TIME: "RECORDING_TIME",
  CONTROL_ALARM: "CONTROL_ALARM",
  LEAVE_WORK_MANAGEMENT: "LEAVE_WORK_MANAGEMENT",
  ETC_OPERATING_TIME: "ETC_OPERATING_TIME",
  WIDGET_SETTING: "WIDGET_SETTING"
} as const;

export type SELECTED_SUB_TAB =
  (typeof SELECTED_SUB_TAB)[keyof typeof SELECTED_SUB_TAB];

interface IWSState {
  isSave: boolean;
  handleIsSave: (value: boolean) => void;
  workingTemplateIdx?: number;
  isSelfBasicWorkType?: boolean;
  workPolicyBasicSetting?: IWorkPolicyBasicSetting;
  workPolicyAdditionalSetting?: IWorkPolicyAdditionalSetting;
  handleWorkPolicyBasicSetting: (payload: IWorkPolicyBasicSetting) => void;
  handleWorkPolicyAdditionalSetting: (
    payload: IWorkPolicyAdditionalSetting
  ) => void;
  handleIsSelfBasicWorkType: (type: boolean) => void;
  handleOtHolidayTemplateNewAutoUse: (value: Is_Use) => void;
  otHolidayTemplateNewAutoUse: Is_Use;
  otWorkingStandardHours: OtWorkingStandardHoursEntity | undefined;
  handleOtWorkingStandardHours: (payload: OtWorkingStandardHoursEntity) => void;
  handleOtWorkingStandardHourList: (
    list: OtWorkingStandardHoursEntity[]
  ) => void;
  setOtWorkingFlexibleBasic: React.Dispatch<
    React.SetStateAction<OtWorkingFlexibleBasicEntity | undefined>
  >;
  setOtWorkingFlexibleHours: React.Dispatch<
    React.SetStateAction<OtWorkingFlexibleHoursEntity[]>
  >;
  setSelfDeterminedWorkHourBasicInfo: React.Dispatch<
    React.SetStateAction<
      AddSelfDeterminedWorkHoursMutationVariables | undefined
    >
  >;
  setWorkingTimeName: React.Dispatch<React.SetStateAction<string[]>>;
  setAutonomyWorkingHourInfo: React.Dispatch<
    React.SetStateAction<
      Exact<AddAutonomyWorkingHourInfoMutationVariables> | undefined
    >
  >;
  commuteRecordInfo: WorkingTemplateCommuteSettingsDraft;
  setCommuteRecordInfo: React.Dispatch<
    React.SetStateAction<WorkingTemplateCommuteSettingsDraft>
  >;
  commuteHydrationStatus: CommuteHydrationStatus;
  setCommuteHydrationStatus: React.Dispatch<
    React.SetStateAction<CommuteHydrationStatus>
  >;
  setControlNotInfo: React.Dispatch<
    React.SetStateAction<
      Exact<AddControlNotificationMutationVariables> | undefined
    >
  >;
  setAbsenceManagementInfo: React.Dispatch<
    React.SetStateAction<
      Exact<AddAbsenceManagementInfoMutationVariables> | undefined
    >
  >;
  setWidgetSettingInfo: React.Dispatch<
    React.SetStateAction<
      Exact<AddWidgetSettingInfoMutationVariables> | undefined
    >
  >;
  setEtcOperatingHours: React.Dispatch<
    React.SetStateAction<
      Exact<AddEtcOperatingHoursMutationVariables> | undefined
    >
  >;
  weekTotalWorkHour?: number;
  setWeekTotalWorkHour: React.Dispatch<React.SetStateAction<number>>;
  selectedSubTab: SELECTED_SUB_TAB;
  setSelectedSubTab: React.Dispatch<React.SetStateAction<SELECTED_SUB_TAB>>;
  basePolicy: WorkingTemplateBasePolicyController;
  basePolicySaveStage: WorkingTemplateSaveStage;
  retryBasePolicyBinding: () => void;
  commuteSaveStage: WorkingTemplateCommuteSaveStage;
  retryCommuteSettings: () => void;
}

export const WS_STORE = createContext<IWSStore>({
  day: Day_Type.Mon
});

export const WS_STATE = createContext<IWSState | undefined>(undefined);

const Container = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  background-color: ${props => props.theme.colors.lightSkyBlue};
  flex-direction: column;
  padding: 10px;
  gap: 20px;
  flex-basis: 100%;
`;

const LoadingContainer = styled.div<{ isLoading?: boolean }>`
  position: fixed;
  display: flex;
  flex-direction: column;
  flex-basis: ${props => (props.isLoading ? "100%" : "0%")};
  visibility: ${props => (props.isLoading ? "visible" : "hidden")};
  left: 45%;
  top: 40%;
  z-index: 2;
`;

const ContentContainer = styled.div<{ isLoading?: boolean }>`
  display: flex;
  flex: ${props => (props.isLoading ? "0" : "1")};
  flex-direction: column;
  visibility: ${props => (props.isLoading ? "hidden" : "visible")};
`;

function buildBasicSaveVariables(
  basic: IWorkPolicyBasicSetting,
  additional: IWorkPolicyAdditionalSetting | undefined,
  isSelfBasicWorkType: boolean
) {
  return {
    name: basic.name,
    workType: basic.workType,
    dayMaxWorkHour: basic.dayMaxWorkHour,
    weekMaxWorkHour: basic.weekMaxWorkHour,
    weekMaxExtWorkHour: basic.weekMaxExtWorkHour,
    mandatoryBreakTermHour: basic.mandatoryBreakTermHour,
    mandatoryBreakMin: basic.mandatoryBreakMin,
    dayOfWeekStart: basic.dayOfWeekStart,
    timeDifferenceMin: basic.timeDifferenceMin,
    periodUnitType: basic.periodUnitType,
    ...(isSelfBasicWorkType
      ? {}
      : { otHoursTypeCategoryIdx: basic.otHoursTypeCategoryIdx }),
    webTemplateWorkTimeUse:
      basic.webTemplateWorkTimeUse ?? Setting_Enable_For_Working_Template.NoUse,
    workingTemplateIdxMinors: additional?.workingTemplateIdxMinors ?? 0,
    periodUnitMonthMinType:
      additional?.periodUnitMonthMinType ??
      Setting_Enable_For_Working_Template.NoUse,
    periodUnitMonthFixMin: additional?.periodUnitMonthFixMin ?? 0,
    standardDailyStartTimeRange: additional?.standardDailyStartTimeRange ?? "",
    standardDailyEndTimeRange: additional?.standardDailyEndTimeRange ?? "",
    nightReferenceStartTimeRange:
      additional?.nightReferenceStartTimeRange ?? "",
    nightReferenceEndTimeRange: additional?.nightReferenceEndTimeRange ?? "",
    lateCheckTime: additional?.lateCheckTime ?? "",
    limitExtworkminDay: additional?.limitExtworkminDay ?? 0,
    limitExtworkminWeek: additional?.limitExtworkminWeek ?? 0,
    limitExtworkminMonth: additional?.limitExtworkminMonth ?? 0,
    weeklyHolidayAllowanceUse:
      additional?.weeklyHolidayAllowanceUse ??
      Setting_Enable_For_Working_Template.NoUse
  };
}

function focusCommuteSettingsField(
  field: keyof WorkingTemplateCommuteSettingsDraft | undefined
) {
  if (!field) return;
  const id =
    field === "unCheckoutAutoCheckoutTime"
      ? "unCheckoutAutoCheckoutTime"
      : `commute-recognition-${field
          .replace(/([A-Z])/g, "-$1")
          .toLowerCase()}`;
  document.getElementById(id)?.focus();
}

function AsonicWorkingSystemScreen() {
  const { id } = useParams();
  const [isSave, setIsSave] = useState<boolean>(false);
  const [selectedSubTab, setSelectedSubTab] = useState<SELECTED_SUB_TAB>(
    SELECTED_SUB_TAB.WORKING_TIME
  );
  const {
    workingTemplateIdx,
    handleWorkingTemplateIdx,
    workPolicyBasicSetting,
    handleWorkPolicyBasicSetting,
    workPolicyAdditionalSetting,
    handleWorkPolicyAdditionalSetting,
    isSelfBasicWorkType,
    otHolidayTemplateNewAutoUse,
    handleOtHolidayTemplateNewAutoUse,
    handleIsSelfBasicWorkType,
    otWorkingStandardHours,
    handleOtWorkingStandardHours,
    otWorkingStandardHourList,
    handleOtWorkingStandardHourList,
    otWorkingFlexibleBasic,
    setOtWorkingFlexibleBasic,
    otWorkingFlexibleHours,
    setOtWorkingFlexibleHours,
    selfDeterminedWorkHourBasicInfo,
    setSelfDeterminedWorkHourBasicInfo,
    workingTimeName,
    setWorkingTimeName,
    autonomyWorkingHourInfo,
    setAutonomyWorkingHourInfo,
    commuteRecordInfo,
    setCommuteRecordInfo,
    commuteHydrationStatus,
    setCommuteHydrationStatus,
    controlNotiInfo,
    setControlNotInfo,
    absenceManagementInfo,
    setAbsenceManagementInfo,
    widgetSettingInfo,
    setWidgetSettingInfo,
    etcOperatingHours,
    setEtcOperatingHours,
    weekTotalWorkHour,
    setWeekTotalWorkHour
  } = useAsonicWorkingSystem();

  const {
    isOpen: isToastMessageOpen,
    handleIsOpen: handleIsToastMessageOpen,
    message,
    toastMessageType,
    handleToast
  } = useOpenToastMessage();

  const [workType, setWorkType] = useState<Work_Type>(
    Work_Type.SFCommutingPolicy
  );
  const [basePolicySaveStage, setBasePolicySaveStage] =
    useState<WorkingTemplateSaveStage>("idle");
  const [commuteSaveStage, setCommuteSaveStage] =
    useState<WorkingTemplateCommuteSaveStage>("idle");
  const savedBasicFingerprint = useRef<string | null>(null);
  const createdTemplateIdx = useRef<number | null>(null);
  const commuteSaveCoordinator = useRef(
    createWorkingTemplateCommuteSaveCoordinator()
  );

  const shouldUseBasePolicy = shouldPersistBasePolicyBinding(
    workType,
    selectedSubTab
  );
  const basePolicyHook = useWorkingTemplateBasePolicy({
    enabled: shouldUseBasePolicy,
    mode:
      workingTemplateIdx && createdTemplateIdx.current !== workingTemplateIdx
        ? "edit"
        : "create",
    workType: 10,
    workingTemplateIdx
  });

  const selectBasePolicy = basePolicyHook.selectPolicy;
  const basePolicy = useMemo<WorkingTemplateBasePolicyController>(
    () => ({
      ...basePolicyHook,
      selectPolicy: policyId => {
        setBasePolicySaveStage("idle");
        selectBasePolicy(policyId);
      }
    }),
    [basePolicyHook, selectBasePolicy]
  );

  const handleIsSave = useCallback((value: boolean) => {
    setIsSave(value);
  }, []);

  const retryBasePolicyBinding = useCallback(() => {
    setIsSave(true);
  }, []);

  const retryCommuteSettings = useCallback(() => {
    setIsSave(true);
  }, []);

  useEffect(() => {
    if (
      basePolicySaveStage !== "partial-failure" &&
      commuteSaveStage !== "partial-failure"
    ) {
      return;
    }
    const warnAboutPartialSave = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue =
        "근무 정책 기본정보는 이미 저장되었지만 후속 설정 저장은 완료되지 않았습니다.";
    };
    window.addEventListener("beforeunload", warnAboutPartialSave);
    return () =>
      window.removeEventListener("beforeunload", warnAboutPartialSave);
  }, [basePolicySaveStage, commuteSaveStage]);

  const reducer = useCallback((_: IReducer, action: { type: Day_Type }) => {
    return {
      day: action.type
    };
  }, []);

  const [state, dispatch] = useReducer(reducer, {
    day: Day_Type.Mon
  });

  const handleWorkType = useCallback((payload: Work_Type) => {
    setWorkType(payload);
  }, []);

  const [addWTBasicInfo, { loading: loading1, client }] =
    useAddWtBasicInfoMutation({
      onError(error) {
        handleApolloErrorLog(error);
        handleToast("근무제를 추가하지 못했습니다.", MessageTypes.ERROR);
      },
      update(_, { data }) {
        if (
          data?.addWTBasicInfo.ok &&
          data?.addWTBasicInfo.workingTemplateIdx
        ) {
          handleWorkingTemplateIdx(data?.addWTBasicInfo.workingTemplateIdx);
        } else if (data?.addWTBasicInfo.error) {
          handleErrorLog(data?.addWTBasicInfo.error);
          handleToast("근무제를 추가하지 못했습니다.", MessageTypes.ERROR);
        }
      }
    });

  const [updateWTBasicInfo, { loading: loading2 }] =
    useUpdateWtBasicInfoMutation({
      onError(error) {
        handleApolloErrorLog(error);
        handleToast("근무제를 수정하지 못했습니다.", MessageTypes.ERROR);
      },
      update(_, { data }) {
        if (!data?.updateWTBasicInfo.ok && data?.updateWTBasicInfo.error) {
          handleErrorLog(data?.updateWTBasicInfo.error);
          handleToast("근무제를 수정하지 못했습니다.", MessageTypes.ERROR);
        }
      }
    });

  const [addStandardWTBasicInfo, { loading: loading3 }] =
    useAddStandardWtBasicInfoMutation({
      onError(error) {
        handleApolloErrorLog(error);
      },
      update(_, { data }) {
        if (data?.addStandardWTBasicInfo.error) {
          handleErrorLog(data?.addStandardWTBasicInfo.error);
        }
      }
    });

  const [addFlexibleWorkingHourInfo, { loading: loading4 }] =
    useAddFlexibleWorkingHourInfoMutation({
      onError(error) {
        handleApolloErrorLog(error);
      },
      update(_, { data }) {
        if (data?.addFlexibleWorkingHourInfo.error) {
          handleErrorLog(data?.addFlexibleWorkingHourInfo.error);
        }
      }
    });

  const [addSelfDeterminedWorkHours, { loading: loading5 }] =
    useAddSelfDeterminedWorkHoursMutation({
      onError(error) {
        handleApolloErrorLog(error);
      },
      update(_, { data }) {
        if (data?.addSelfDeterminedWorkHours.error) {
          handleErrorLog(data?.addSelfDeterminedWorkHours.error);
        }
      }
    });

  const [addAutonomyWorkingHourInfo, { loading: loading6 }] =
    useAddAutonomyWorkingHourInfoMutation({
      onError(error) {
        handleApolloErrorLog(error);
      },
      update(_, { data }) {
        if (data?.addAutonomyWorkingHourInfo.error) {
          handleErrorLog(data?.addAutonomyWorkingHourInfo.error);
        }
      }
    });

  const [addControlNotification, { loading: loading8 }] =
    useAddControlNotificationMutation({
      onError(error) {
        handleApolloErrorLog(error);
      },
      update(_, { data }) {
        if (data?.addControlNotification.error) {
          handleErrorLog(data?.addControlNotification.error);
        }
      }
    });

  const [addAbsenceManagementInfo, { loading: loading9 }] =
    useAddAbsenceManagementInfoMutation({
      onError(error) {
        handleApolloErrorLog(error);
      },
      update(_, { data }) {
        if (data?.addAbsenceManagementInfo.error) {
          handleErrorLog(data?.addAbsenceManagementInfo.error);
        }
      }
    });

  const [addWidgetSettingInfo, { loading: loading10 }] =
    useAddWidgetSettingInfoMutation({
      onError(error) {
        handleApolloErrorLog(error);
      },
      update(_, { data }) {
        if (data?.addWidgetSettingInfo.error) {
          handleErrorLog(data?.addWidgetSettingInfo.error);
        }
      }
    });

  const [addEtcOperatingHours, { loading: loading11 }] =
    useAddEtcOperatingHoursMutation({
      onError(error) {
        handleApolloErrorLog(error);
      },
      update(_, { data }) {
        if (data?.addEtcOperatingHours.error) {
          handleErrorLog(data?.addEtcOperatingHours.error);
        }
      }
    });

  const loading = useMemo(() => {
    return (
      loading1 ||
      loading2 ||
      loading3 ||
      loading4 ||
      loading5 ||
      loading6 ||
      loading8 ||
      loading9 ||
      loading10 ||
      loading11 ||
      commuteSaveStage === "basic-saving" ||
      commuteSaveStage === "commute-saving"
    );
  }, [
    loading1,
    loading2,
    loading3,
    loading4,
    loading5,
    loading6,
    loading8,
    loading9,
    loading10,
    loading11,
    commuteSaveStage
  ]);

  useEffect(() => {
    if (isSave && workPolicyBasicSetting) {
      setIsSave(false);
      const basicVariables = buildBasicSaveVariables(
        workPolicyBasicSetting,
        workPolicyAdditionalSetting,
        isSelfBasicWorkType ?? false
      );

      if (selectedSubTab === SELECTED_SUB_TAB.RECORDING_TIME) {
        if (commuteSaveCoordinator.current.isRunning()) return;
        if (commuteHydrationStatus !== "ready") {
          handleToast(
            "출퇴근 설정을 불러온 뒤 다시 저장해 주세요.",
            MessageTypes.ERROR
          );
          return;
        }

        const fingerprint = JSON.stringify(basicVariables);
        void commuteSaveCoordinator.current
          .run({
            selectedSubTab,
            workingTemplateIdx:
              workingTemplateIdx ||
              workPolicyBasicSetting.workingTemplateIdx ||
              undefined,
            draft: commuteRecordInfo,
            basicFingerprint: fingerprint,
            savedBasicFingerprint: savedBasicFingerprint.current,
            createBasic: async () => {
              const result = await addWTBasicInfo({ variables: basicVariables });
              const response = result.data?.addWTBasicInfo;
              if (!response?.ok || !response.workingTemplateIdx) {
                throw new Error(
                  response?.error ?? "근무 정책 기본정보를 추가하지 못했습니다."
                );
              }
              return response.workingTemplateIdx;
            },
            updateBasic: async templateIdx => {
              const result = await updateWTBasicInfo({
                variables: {
                  ...basicVariables,
                  workingTemplateIdx: templateIdx,
                  otHolidayTemplateNewAutoUse
                }
              });
              const response = result.data?.updateWTBasicInfo;
              if (!response?.ok) {
                throw new Error(
                  response?.error ?? "근무 정책 기본정보를 수정하지 못했습니다."
                );
              }
            },
            putCommute: async (templateIdx, draft) =>
              (
                await workingTemplateCommuteSettingsApi.put(
                  templateIdx,
                  draft
                )
              ).data,
            onStage: setCommuteSaveStage,
            onBasicSaved: (templateIdx, savedFingerprint) => {
              savedBasicFingerprint.current = savedFingerprint;
              if (!workingTemplateIdx) {
                createdTemplateIdx.current = templateIdx;
              }
              handleWorkingTemplateIdx(templateIdx);
            },
            onCommuteSaved: settings => {
              const { workingTemplateIdx: _templateIdx, ...draft } = settings;
              setCommuteRecordInfo(draft);
              setCommuteHydrationStatus("ready");
            },
            onCommuteFailed: () => undefined
          })
          .then(result => {
            if (result.status === "partial-failure") {
              handleToast(
                "기본정보는 저장했지만 출퇴근 설정 저장에 실패했습니다. 다시 시도해 주세요.",
                MessageTypes.ERROR
              );
              return;
            }
            handleToast(
              workPolicyBasicSetting.workingTemplateIdx
                ? "성공적으로 근무제를 수정하였습니다."
                : "성공적으로 근무제를 추가하였습니다.",
              MessageTypes.SUCCESS
            );
            void client.resetStore();
          })
          .catch(error => {
            setCommuteSaveStage("idle");
            handleToast(
              error instanceof CommuteSettingsValidationError
                ? error.message
                : "근무 정책 기본정보를 저장하지 못했습니다.",
              MessageTypes.ERROR
            );
            if (error instanceof CommuteSettingsValidationError) {
              focusCommuteSettingsField(error.firstField);
            }
          });
        return;
      }

      if (shouldUseBasePolicy && workPolicyBasicSetting.name) {
        const fingerprint = JSON.stringify(basicVariables);
        void saveWorkingTemplatePolicyBinding({
          workType,
          selectedSubTab,
          workingTemplateIdx:
            workingTemplateIdx ||
            workPolicyBasicSetting.workingTemplateIdx ||
            undefined,
          selectedPolicyId: basePolicy.state.selectedPolicyId,
          selectedPolicyDetail: basePolicy.state.detail,
          detailStatus: basePolicy.state.detailStatus,
          basicFingerprint: fingerprint,
          savedBasicFingerprint: savedBasicFingerprint.current,
          basicAlreadySaved:
            basePolicy.state.basicSaved ||
            savedBasicFingerprint.current !== null,
          createBasic: async () => {
            const result = await addWTBasicInfo({ variables: basicVariables });
            const response = result.data?.addWTBasicInfo;
            if (!response?.ok || !response.workingTemplateIdx) {
              throw new Error(
                response?.error ?? "근무 정책 기본정보를 추가하지 못했습니다."
              );
            }
            return response.workingTemplateIdx;
          },
          updateBasic: async templateIdx => {
            const result = await updateWTBasicInfo({
              variables: {
                ...basicVariables,
                workingTemplateIdx: templateIdx,
                otHolidayTemplateNewAutoUse
              }
            });
            const response = result.data?.updateWTBasicInfo;
            if (!response?.ok) {
              throw new Error(
                response?.error ?? "근무 정책 기본정보를 수정하지 못했습니다."
              );
            }
          },
          putBinding: async (templateIdx, workPolicyId) =>
            (
              await workingTemplatePolicyApi.putBasePolicyBinding(templateIdx, {
                workPolicyId
              })
            ).data,
          onStage: setBasePolicySaveStage,
          onBasicSaved: (templateIdx, savedFingerprint) => {
            savedBasicFingerprint.current = savedFingerprint;
            if (!workingTemplateIdx) {
              createdTemplateIdx.current = templateIdx;
            }
            handleWorkingTemplateIdx(templateIdx);
            basePolicy.markBasicSaved(templateIdx);
          },
          onBindingSaved: basePolicy.markBindingSaved,
          onBindingFailed: basePolicy.markBindingFailed
        })
          .then(result => {
            if (result.status !== "success") return;
            handleToast(
              workPolicyBasicSetting.workingTemplateIdx
                ? "성공적으로 근무제를 수정하였습니다."
                : "성공적으로 근무제를 추가하였습니다.",
              MessageTypes.SUCCESS
            );
            void client.resetStore();
          })
          .catch(error => {
            setBasePolicySaveStage("idle");
            handleToast(
              error instanceof WorkingTemplatePolicySaveValidationError
                ? error.message
                : "근무 정책 기본정보를 저장하지 못했습니다.",
              MessageTypes.ERROR
            );
            if (error instanceof WorkingTemplatePolicySaveValidationError) {
              document.getElementById("working-template-base-policy")?.focus();
            }
          });
        return;
      }

      if (
        workPolicyBasicSetting.name &&
        !workPolicyBasicSetting.workingTemplateIdx
      ) {
        Promise.all([
          addWTBasicInfo({
            variables: {
              name: workPolicyBasicSetting.name,
              workType: workPolicyBasicSetting.workType,
              dayMaxWorkHour: workPolicyBasicSetting.dayMaxWorkHour,
              weekMaxWorkHour: workPolicyBasicSetting.weekMaxWorkHour,
              weekMaxExtWorkHour: workPolicyBasicSetting.weekMaxExtWorkHour,
              mandatoryBreakTermHour:
                workPolicyBasicSetting.mandatoryBreakTermHour,
              mandatoryBreakMin: workPolicyBasicSetting.mandatoryBreakMin,
              dayOfWeekStart: workPolicyBasicSetting.dayOfWeekStart,
              timeDifferenceMin: workPolicyBasicSetting.timeDifferenceMin,
              periodUnitType: workPolicyBasicSetting.periodUnitType,
              ...(isSelfBasicWorkType
                ? {}
                : {
                    otHoursTypeCategoryIdx:
                      workPolicyBasicSetting.otHoursTypeCategoryIdx
                  }),
              webTemplateWorkTimeUse:
                workPolicyBasicSetting?.webTemplateWorkTimeUse ??
                Setting_Enable_For_Working_Template.NoUse,
              workingTemplateIdxMinors:
                workPolicyAdditionalSetting?.workingTemplateIdxMinors ?? 0,
              periodUnitMonthMinType:
                workPolicyAdditionalSetting?.periodUnitMonthMinType ??
                Setting_Enable_For_Working_Template.NoUse,
              periodUnitMonthFixMin:
                workPolicyAdditionalSetting?.periodUnitMonthFixMin ?? 0,
              standardDailyStartTimeRange:
                workPolicyAdditionalSetting?.standardDailyStartTimeRange ?? "",
              standardDailyEndTimeRange:
                workPolicyAdditionalSetting?.standardDailyEndTimeRange ?? "",
              nightReferenceStartTimeRange:
                workPolicyAdditionalSetting?.nightReferenceStartTimeRange ?? "",
              nightReferenceEndTimeRange:
                workPolicyAdditionalSetting?.nightReferenceEndTimeRange ?? "",
              lateCheckTime: workPolicyAdditionalSetting?.lateCheckTime ?? "",
              limitExtworkminDay:
                workPolicyAdditionalSetting?.limitExtworkminDay ?? 0,
              limitExtworkminWeek:
                workPolicyAdditionalSetting?.limitExtworkminWeek ?? 0,
              limitExtworkminMonth:
                workPolicyAdditionalSetting?.limitExtworkminMonth ?? 0,
              weeklyHolidayAllowanceUse:
                workPolicyAdditionalSetting?.weeklyHolidayAllowanceUse ??
                Setting_Enable_For_Working_Template.NoUse
            }
          })
        ]).then(() => {
          setBasePolicySaveStage("idle");
          savedBasicFingerprint.current = null;
          createdTemplateIdx.current = null;
          handleToast(
            "성공적으로 근무제를 추가하였습니다.",
            MessageTypes.SUCCESS
          );
        });
      }

      if (
        workPolicyBasicSetting.name &&
        workPolicyBasicSetting.workingTemplateIdx
      ) {
        const workingTemplateIdx =
          workPolicyBasicSetting.workingTemplateIdx as number;
        const parsedOtWorkingStandardHourList = otWorkingStandardHourList.map(
          item => ({
            breaktime_type: item.breakTimeType,
            day_of_week: item.dayOfWeek,
            day_of_week_type: item.dayOfWeekType,
            end_breaktime: item.endBreakTime,
            end_time: item.endTime,
            start_breaktime: item.startBreakTime,
            start_time: item.startTime,
            time_sequence: item.timeSequence,
            work_week_select: item.workWeekSelect,
            work_week_type: item.workWeekType
          })
        );
        const parsedOtWorkingFlexibleHours = otWorkingFlexibleHours.map(
          item => ({
            day_of_week: item.dayOfWeek,
            end_breaktime: item.endBreakTime,
            end_time: item.endTime,
            start_breaktime: item.startBreakTime,
            start_time: item.startTime,
            week_sequence: item.weekSequence,
            work_hour: item.workHour
          })
        );
        let mutationArr: any[] = [
          updateWTBasicInfo({
            variables: {
              name: workPolicyBasicSetting.name,
              workType: workPolicyBasicSetting.workType,
              dayMaxWorkHour: workPolicyBasicSetting.dayMaxWorkHour,
              weekMaxWorkHour: workPolicyBasicSetting.weekMaxWorkHour,
              weekMaxExtWorkHour: workPolicyBasicSetting.weekMaxExtWorkHour,
              mandatoryBreakTermHour:
                workPolicyBasicSetting.mandatoryBreakTermHour,
              mandatoryBreakMin: workPolicyBasicSetting.mandatoryBreakMin,
              dayOfWeekStart: workPolicyBasicSetting.dayOfWeekStart,
              timeDifferenceMin: workPolicyBasicSetting.timeDifferenceMin,
              periodUnitType: workPolicyBasicSetting.periodUnitType,
              ...(isSelfBasicWorkType
                ? {}
                : {
                    otHoursTypeCategoryIdx:
                      workPolicyBasicSetting.otHoursTypeCategoryIdx
                  }),
              workingTemplateIdx,
              otHolidayTemplateNewAutoUse,
              webTemplateWorkTimeUse:
                workPolicyBasicSetting?.webTemplateWorkTimeUse ??
                Setting_Enable_For_Working_Template.NoUse,
              workingTemplateIdxMinors:
                workPolicyAdditionalSetting?.workingTemplateIdxMinors ?? 0,
              periodUnitMonthMinType:
                workPolicyAdditionalSetting?.periodUnitMonthMinType ??
                Setting_Enable_For_Working_Template.NoUse,
              periodUnitMonthFixMin:
                workPolicyAdditionalSetting?.periodUnitMonthFixMin ?? 0,
              standardDailyStartTimeRange:
                workPolicyAdditionalSetting?.standardDailyStartTimeRange ?? "",
              standardDailyEndTimeRange:
                workPolicyAdditionalSetting?.standardDailyEndTimeRange ?? "",
              nightReferenceStartTimeRange:
                workPolicyAdditionalSetting?.nightReferenceStartTimeRange ?? "",
              nightReferenceEndTimeRange:
                workPolicyAdditionalSetting?.nightReferenceEndTimeRange ?? "",
              lateCheckTime: workPolicyAdditionalSetting?.lateCheckTime ?? "",
              limitExtworkminDay:
                workPolicyAdditionalSetting?.limitExtworkminDay ?? 0,
              limitExtworkminWeek:
                workPolicyAdditionalSetting?.limitExtworkminWeek ?? 0,
              limitExtworkminMonth:
                workPolicyAdditionalSetting?.limitExtworkminMonth ?? 0,
              weeklyHolidayAllowanceUse:
                workPolicyAdditionalSetting?.weeklyHolidayAllowanceUse ??
                Setting_Enable_For_Working_Template.NoUse
            }
          })
        ];
        if (
          otWorkingStandardHours &&
          selectedSubTab === SELECTED_SUB_TAB.WORKING_TIME
        ) {
          const {
            startTime,
            endTime,
            workWeekType,
            workWeekSelect,
            dayOfWeekType,
            dayOfWeek,
            startBreakTime,
            endBreakTime
          } = otWorkingStandardHours;
          mutationArr.push(
            addStandardWTBasicInfo({
              variables: {
                workingTemplateIdx,
                startTime,
                endTime,
                workWeekType,
                workWeekSelect,
                dayOfWeekType,
                dayOfWeek,
                startBreakTime,
                endBreakTime,
                otWorkingStandardHourList: parsedOtWorkingStandardHourList
              }
            })
          );
        }

        if (
          otWorkingFlexibleBasic &&
          selectedSubTab === SELECTED_SUB_TAB.WORKING_TIME
        ) {
          addFlexibleWorkingHourInfo({
            variables: {
              ...otWorkingFlexibleBasic,
              otWorkingFlexibleHours: parsedOtWorkingFlexibleHours,
              workingTemplateIdx
            }
          });
          mutationArr.push(addFlexibleWorkingHourInfo);
        }
        if (
          selfDeterminedWorkHourBasicInfo &&
          selectedSubTab === SELECTED_SUB_TAB.WORKING_TIME
        ) {
          mutationArr.push(
            addSelfDeterminedWorkHours({
              variables: { ...selfDeterminedWorkHourBasicInfo, workingTimeName }
            })
          );
        }
        if (
          autonomyWorkingHourInfo &&
          selectedSubTab === SELECTED_SUB_TAB.WORKING_TIME
        ) {
          mutationArr.push(
            addAutonomyWorkingHourInfo({
              variables: autonomyWorkingHourInfo
            })
          );
        }
        if (
          controlNotiInfo &&
          selectedSubTab === SELECTED_SUB_TAB.CONTROL_ALARM
        ) {
          mutationArr.push(
            addControlNotification({
              variables: controlNotiInfo
            })
          );
        }
        if (
          absenceManagementInfo &&
          selectedSubTab === SELECTED_SUB_TAB.LEAVE_WORK_MANAGEMENT
        ) {
          mutationArr.push(
            addAbsenceManagementInfo({
              variables: absenceManagementInfo
            })
          );
        }
        if (
          widgetSettingInfo &&
          selectedSubTab === SELECTED_SUB_TAB.WIDGET_SETTING
        ) {
          mutationArr.push(
            addWidgetSettingInfo({ variables: widgetSettingInfo })
          );
        }
        if (
          etcOperatingHours &&
          selectedSubTab === SELECTED_SUB_TAB.ETC_OPERATING_TIME
        ) {
          mutationArr.push(
            addEtcOperatingHours({
              variables: etcOperatingHours
            })
          );
        }
        Promise.all(mutationArr).then(() => {
          setBasePolicySaveStage("idle");
          savedBasicFingerprint.current = null;
          createdTemplateIdx.current = null;
          handleToast(
            "성공적으로 근무제를 수정하였습니다.",
            MessageTypes.SUCCESS
          );
          void client.resetStore();
        });
      }
    }
  }, [
    isSave,
    addWTBasicInfo,
    updateWTBasicInfo,
    otHolidayTemplateNewAutoUse,
    isSelfBasicWorkType,
    otWorkingStandardHours,
    otWorkingStandardHourList,
    addFlexibleWorkingHourInfo,
    otWorkingFlexibleBasic,
    otWorkingFlexibleHours,
    selfDeterminedWorkHourBasicInfo,
    addSelfDeterminedWorkHours,
    workingTimeName,
    autonomyWorkingHourInfo,
    addAutonomyWorkingHourInfo,
    commuteRecordInfo,
    commuteHydrationStatus,
    setCommuteRecordInfo,
    setCommuteHydrationStatus,
    commuteSaveStage,
    controlNotiInfo,
    addControlNotification,
    absenceManagementInfo,
    addAbsenceManagementInfo,
    widgetSettingInfo,
    addWidgetSettingInfo,
    etcOperatingHours,
    addEtcOperatingHours,
    client,
    selectedSubTab,
    shouldUseBasePolicy,
    workType,
    workingTemplateIdx,
    basePolicy,
    handleWorkingTemplateIdx,
    handleToast,
    workPolicyAdditionalSetting
  ]);

  useEffect(() => {
    if (id) {
      handleWorkingTemplateIdx(parseInt(id));
    }
  }, [id, handleWorkingTemplateIdx]);

  return (
    <WS_STATE.Provider
      value={{
        isSave,
        handleIsSave,
        workingTemplateIdx,
        workPolicyBasicSetting, // 기본설정 탭
        handleWorkPolicyBasicSetting, // 기본설정 탭 정보 저장
        workPolicyAdditionalSetting, // 추가설정 탭
        handleWorkPolicyAdditionalSetting, // 추가설정 탭 정보 저장
        isSelfBasicWorkType,
        handleIsSelfBasicWorkType,
        otHolidayTemplateNewAutoUse,
        handleOtHolidayTemplateNewAutoUse,
        otWorkingStandardHours,
        handleOtWorkingStandardHours,
        handleOtWorkingStandardHourList,
        setOtWorkingFlexibleBasic,
        setOtWorkingFlexibleHours,
        setSelfDeterminedWorkHourBasicInfo,
        setWorkingTimeName,
        setAutonomyWorkingHourInfo,
        commuteRecordInfo,
        setCommuteRecordInfo,
        commuteHydrationStatus,
        setCommuteHydrationStatus,
        setControlNotInfo,
        setAbsenceManagementInfo,
        setWidgetSettingInfo,
        setEtcOperatingHours,
        weekTotalWorkHour,
        setWeekTotalWorkHour,
        selectedSubTab,
        setSelectedSubTab,
        basePolicy,
        basePolicySaveStage,
        retryBasePolicyBinding,
        commuteSaveStage,
        retryCommuteSettings
      }}
    >
      <WS_STORE.Provider value={{ ...state, dispatch }}>
        <Container>
          <LoadingContainer isLoading={loading}>
            <ReactLoading
              type="spokes"
              color={colors.green}
              height={200}
              width={170}
            />
            <span>{`저장 중입니다. 잠시만 기다려 주세요`}</span>
          </LoadingContainer>
          <ContentContainer isLoading={loading}>
            <TopTabs workType={workType} handleWorkType={handleWorkType} />
            <WSTabs workType={workType} />
          </ContentContainer>
          <ToastMessage
            message={message}
            isOpen={isToastMessageOpen}
            handleIsOpen={handleIsToastMessageOpen}
            messageTypes={toastMessageType}
          />
        </Container>
      </WS_STORE.Provider>
    </WS_STATE.Provider>
  );
}

export default AsonicWorkingSystemScreen;
